<script setup lang="ts">
// Duplicated from v-dropdown-improved
import { onClickOutside, useConfirmDialog, useMagicKeys } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { VButton, VIcon } from '@/modules/shared/components'

// NOTE: this is duplicated in multiple places
// TODO: move to shared types
type Option = {
  disabled?: boolean
  label?: string
  text?: string
  value: string
}

const props = withDefaults(
  defineProps<{
    aligned?: string
    hideDisabled?: boolean
    multi?: boolean
    options: Option[]
    onChange?: any
    onClose?: any
    closeOnSelect?: boolean
    assumeAllSelected?: boolean
  }>(),
  {
    aligned: 'left',
    hideDisabled: false,
    multi: true,
    onChange: () => {},
    onClose: () => {},
    assumeAllSelected: true,
  },
)

const modelValue = defineModel<string[]>({ default: () => [] })

const { isRevealed, reveal: revealDialog, cancel: cancelDialog } = useConfirmDialog()

const optionValues = computed(() => props.options.map((option) => option.value))

// Initialize with all options selected by default when configured
watch(
  optionValues,
  (vals) => {
    if (props.multi && props.assumeAllSelected && modelValue.value.length === 0 && vals.length > 0) {
      modelValue.value = [...vals]
    }
  },
  { immediate: true },
)

const shouldCloseOnSelect = computed(() => (props.closeOnSelect ?? !props.multi) === true)

function isSelected(value: string) {
  return modelValue.value.includes(value)
}

const allSelected = computed(() => props.options?.length === modelValue.value?.length)

const toggleSelect = (value: string) => {
  let newValue: string[]

  if (props.multi) {
    newValue = isSelected(value) ? modelValue.value.filter((val) => val !== value) : [...modelValue.value, value]
  } else {
    newValue = [value]
  }

  modelValue.value = newValue
  if (props.onChange) props.onChange(newValue)
  if (shouldCloseOnSelect.value) {
    cancel(newValue)
  }
}

const toggleAll = () => {
  const next = allSelected.value ? [] : optionValues.value
  modelValue.value = next
  if (props.onChange) props.onChange(next)
}

const valuesBefore = ref<string[]>([])
const valuesAfter = ref<string[]>([])

const reveal = () => {
  valuesBefore.value = [...modelValue.value]
  revealDialog()
}

const cancel = (nextValues?: string[]) => {
  valuesAfter.value = nextValues ? [...nextValues] : [...modelValue.value]
  if (props.onClose && isRevealed.value) props.onClose(valuesBefore.value, valuesAfter.value)
  cancelDialog()
}

const target = ref(null)

onClickOutside(target, (event) => cancel())

const { escape } = useMagicKeys()

watch(escape, (v) => {
  if (v) {
    cancel()
  }
})
</script>

<template>
  <div
    class="relative z-50"
    :class="aligned === 'left' ? 'text-left' : 'text-right'"
    ref="target"
    :aria-expanded="isRevealed"
  >
    <VButton
      @click="isRevealed ? cancel() : reveal()"
      @keydown.enter.prevent="isRevealed ? cancel() : reveal()"
      @keydown.space.prevent="isRevealed ? cancel() : reveal()"
    >
      <div class="flex items-center gap-1">
        <slot name="default"></slot>
        <VIcon name="ChevronDown" class="h-4 w-4 text-gray-500" />
      </div>
    </VButton>

    <ul
      v-show="isRevealed"
      class="absolute z-50 mt-1 max-h-[480px] min-w-[220px] divide-y-0 divide-gray-100 overflow-y-auto rounded-md bg-white shadow-lg ring-[1px] ring-gray-700/20"
      :class="aligned === 'left' ? 'left-0' : 'right-0'"
    >
      <li
        v-if="multi"
        @click="toggleAll"
        class="flex cursor-pointer items-center space-x-2 border-b px-3 py-1.5 pr-6 text-sm text-gray-700 hover:bg-gray-100"
      >
        <div class="flex h-4 w-4 items-center justify-center rounded border bg-white">
          <VIcon name="Check" class="h-3 w-3" :class="{ invisible: !allSelected }" />
        </div>
        <div class="font-semibold">{{ allSelected ? 'Deselect' : 'Select' }} all</div>
      </li>
      <li
        v-for="option in options"
        v-show="!(option.disabled && hideDisabled)"
        :key="option.value"
        class="relative flex cursor-pointer items-center space-x-2 whitespace-nowrap px-3 py-1.5 pr-6 text-sm text-gray-700 hover:bg-gray-100"
        :class="{ 'cursor-not-allowed opacity-50': option.disabled }"
        @click="option.disabled ? '' : toggleSelect(option.value)"
      >
        <span class="text-gray-500" :class="{ invisible: !isSelected(option.value) }">
          <VIcon name="Check" />
        </span>
        <div class="font-medium" :class="[!isSelected(option.value) && multi ? 'text-gray-500' : '']">
          {{ option.label || option.text }}
        </div>
      </li>
    </ul>
  </div>
</template>
